import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { postLoginPath } from "@/lib/moduleHost";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
export default function MagicLinkVerifyPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("verifying");   // verifying | ok | err
  const [errMsg, setErrMsg] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;     // guard against React 18 double-mount
    ran.current = true;
    const token = params.get("token");
    if (!token) {
      setState("err");
      setErrMsg("This URL doesn't contain a valid sign-in token.");
      return;
    }
    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/api/auth/magic-link/verify`,
          { token },
          { withCredentials: true },
        );
        setState("ok");
        if (typeof refresh === "function") { try { await refresh(); } catch (_) {} }
        // Small pause so the "Signed in" confirmation is visible.
        const role = data?.role || "customer";
        const target = postLoginPath(role);
        setTimeout(() => nav(target, { replace: true }), 800);
      } catch (err) {
        setState("err");
        setErrMsg(err?.response?.data?.detail
                    || "This sign-in link is invalid or expired.");
      }
    })();
  }, [params, nav, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-4">
      <div
        data-testid={`magic-verify-${state}`}
        className="w-full max-w-md bg-white rounded-2xl border border-[#E8E5DF] p-8 md:p-10 text-center"
      >
        {state === "verifying" && (
          <>
            <Loader2 size={40} className="animate-spin mx-auto text-[#C05746]"/>
            <h1 className="font-serif-display text-2xl mt-6">Signing you in…</h1>
            <p className="text-sm text-[#5C625E] mt-2">Verifying your link.</p>
          </>
        )}
        {state === "ok" && (
          <>
            <CheckCircle2 size={40} className="mx-auto text-emerald-600"/>
            <h1 className="font-serif-display text-2xl mt-6">Signed in</h1>
            <p className="text-sm text-[#5C625E] mt-2">
              Redirecting you to your account…
            </p>
          </>
        )}
        {state === "err" && (
          <>
            <XCircle size={40} className="mx-auto text-[#C05746]"/>
            <h1 className="font-serif-display text-2xl mt-6">Link no longer valid</h1>
            <p className="text-sm text-[#5C625E] mt-2">{errMsg}</p>
            <Link to="/login"
                  className="mt-6 inline-block text-[#C05746] hover:underline">
              Request a new sign-in link →
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
